import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiSearch, FiFileText, FiClock, FiRefreshCw, FiTrendingUp,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import UpsellIcon from '../../assets/images/Upsell Indicator 1.svg';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import '../../styles/UpsellIndicatorHistory.css';
const PAGE_SIZE  = 10;
const getToken   = () => localStorage.getItem('toolboxToken') || '';

interface HistoryRow {
  id: string;
  name: string;
  created: string;
  lob: string;
}

interface SummaryData {
  upsell_indicator: string;
  overall_recommendation: string;
  summary: string;
  clientName: string;
}

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

const extractJsonFromMarkdown = (md: string) => {
  const match = md.match(/```json\s*([\s\S]*?)\s*```/);
  if (match) {
    try { return JSON.parse(match[1].trim()); } catch { /* fall through */ }
  }
  const upsellMatch = md.match(/"upsell_indicator":\s*"([^"]+)"/);
  const recMatch    = md.match(/"overall_recommendation":\s*"([^"]+)"/);
  if (upsellMatch || recMatch) {
    return {
      upsell_indicator:       upsellMatch?.[1] || '',
      overall_recommendation: recMatch?.[1]    || '',
    };
  }
  return null;
};

const cleanSummaryMarkdown = (raw: string): string => {
  let text = raw;
  if (text.includes('$$POLICY_UPSELL_INDICATOR_START')) {
    text = text.split('$$POLICY_UPSELL_INDICATOR_START')[0];
  }
  [
    /### Analysis[\s\S]*?(?=### |---)/,
    /RAG Context[^:]*:[\s\S]*?---/,
    /```json[\s\S]*?```/g,
    /### (JSON Output|MARKDOWN TABLE)\s*/g,
  ].forEach(p => { text = text.replace(p, ''); });
  return text.trim().replace(/\n{3,}/g, '\n\n');
};

/* ── Preview Modal (portal) ───────────────────────────────────────────── */
interface PreviewModalProps {
  summary: SummaryData | null;
  loading: boolean;
  onClose: () => void;
}



const mdComponents = {
  table: ({ children }: any) => (
    <div className="upsell-indicator-history__md-table-wrap">
      <table className="upsell-indicator-history__md-table">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="upsell-indicator-history__md-thead">{children}</thead>
  ),
  th: ({ children }: any) => (
    <th className="upsell-indicator-history__md-th">
      {children}
    </th>
  ),
  tr: ({ children }: any) => (
    <tr className="upsell-indicator-history__md-tr">{children}</tr>
  ),
  td: ({ children }: any) => (
    <td className="upsell-indicator-history__md-td">
      {children}
    </td>
  ),
};

const PreviewModal: React.FC<PreviewModalProps> = ({ summary, loading, onClose }) => {
  const panel = (
    <div className="upsell-indicator-history__modal-panel">
      {/* Header */}
      <div className="upsell-indicator-history__modal-header">
        <div className="upsell-indicator-history__modal-header-left">
          <FiTrendingUp size={15} style={{ color: 'var(--accent-purple)' }} />
          <span className="upsell-indicator-history__modal-title">
            Policy Upsell Summary
            {summary?.clientName && (
              <span className="upsell-indicator-history__modal-title-client">
                — {summary.clientName}
              </span>
            )}
          </span>
        </div>
        <button
          onClick={onClose}
          className="upsell-indicator-history__modal-close-btn"
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="upsell-indicator-history__modal-body">
        {loading ? (
          <div className="upsell-indicator-history__modal-loading">
            <div className="upsell-indicator-history__modal-spinner" />
            <span className="upsell-indicator-history__modal-loading-text">Loading summary…</span>
          </div>
        ) : summary ? (
          <div className="upsell-indicator-history__modal-summary">
            {/* Upsell Indicator badge */}
            {summary.upsell_indicator && (
              <div className="mb-3 upsell-indicator-history__indicator-badge-wrap">
                <span className="fw-bold">Upsell Indicator: </span>
                <span
                  className={`badge ${
                    summary.upsell_indicator.toLowerCase() === 'yes'
                      ? 'bg-success'
                      : 'bg-secondary'
                  }`}
                >
                  {summary.upsell_indicator}
                </span>
              </div>
            )}

            {/* Overall Recommendation */}
            {summary.overall_recommendation && (
              <div
                className="alert alert-info mb-3 upsell-indicator-history__recommendation-alert"
              >
                <strong>Overall Recommendation:</strong>
                <p className="mb-0 mt-2">{summary.overall_recommendation}</p>
              </div>
            )}

            {/* Markdown body */}
            <div className="upsell-indicator-history__markdown-body">
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm]}
                components={mdComponents}
              >
                {summary.summary}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="upsell-indicator-history__no-summary">
            No summary available
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <div
      className="csams-overlay upsell-indicator-history__overlay-zindex"
      onClick={onClose}
    >
      <div onClick={e => e.stopPropagation()}>{panel}</div>
    </div>,
    document.body,
  );
};

/* ── Main Component ───────────────────────────────────────────────────── */
const UpsellIndicatorHistory: React.FC = () => {
  const navigate = useNavigate();

  const [rows,    setRows]    = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [search,  setSearch]  = useState('');
  const [page,    setPage]    = useState(1);

  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [summary, setSummary] = useState<SummaryData | null>(null);

  const { showError } = useToast();

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAMSAI/upsellsummary-history`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        signal,
      });
      const data: any[] = res.data || [];
      const mapped: HistoryRow[] = data.map(item => {
        let commercialName = 'N/A';
        try {
          const fixed = (item.insuredDetails || '')
            .replace(/'/g, '"').replace(/None/g, 'null')
            .replace(/True/g, 'true').replace(/False/g, 'false');
          const parsed = JSON.parse(fixed);
          if (Array.isArray(parsed) && parsed[0]?.commercialName) {
            commercialName = parsed[0].commercialName;
          }
        } catch { /* ignore */ }
        return {
          id:      String(item.id),
          name:    commercialName,
          created: item.created || '',
          lob:     item.lineOfBusiness || item.lob || item.policyType || 'N/A',
        };
      });
      setRows(mapped);
    } catch (e: any) {
      if (e.name === 'AbortError' || axios.isCancel(e)) return;
      if (e.response?.status === 401) { navigate('/login'); return; }
      setError(e.message || 'Failed to load history');
      showError('Failed to load upsell indicator history');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let controller: AbortController | undefined;
    const timer = setTimeout(() => {
      controller = new AbortController();
      fetchData(controller.signal);
    }, 0);
    return () => { clearTimeout(timer); controller?.abort(); };
  }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.name.toLowerCase().includes(q) || r.lob.toLowerCase().includes(q),
    );
  }, [rows, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  const openSummary = async (row: HistoryRow) => {
    setShowPreview(true);
    setPreviewLoading(true);
    setSummary(null);
    try {
      const res = await axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAMSAI/upsellsummary-by-id/${row.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = Array.isArray(res.data) ? res.data[0] : res.data;
      if (!data?.policyUpsellSummary) { setPreviewLoading(false); return; }
      const extracted = extractJsonFromMarkdown(data.policyUpsellSummary);
      const clean     = cleanSummaryMarkdown(data.policyUpsellSummary);
      setSummary({
        upsell_indicator:       extracted?.upsell_indicator       || '',
        overall_recommendation: extracted?.overall_recommendation || '',
        summary:                clean,
        clientName:             row.name,
      });
    } catch (e: any) {
      if (e.response?.status === 401) navigate('/login');
      else showError('Failed to load summary');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => { setShowPreview(false); setSummary(null); };

  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title upsell-indicator-history__title-margin">Upsell Indicator History</h3>
          <div className="upsell-indicator-history__header-actions">
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input
                type="text"
                placeholder="Search by name or LOB…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData()}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state upsell-indicator-history__error-state">
              <FiFileText size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader upsell-indicator-history__loader-min-height">
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching upsell indicator records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiFileText size={28} />
              <p>{search ? 'No records match your search.' : 'No upsell indicator history yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">Client Name</th>
                    <th className="qp-table__th">Date</th>
                    <th className="qp-table__th">Line of Business</th>
                    <th className="qp-table__th upsell-indicator-history__center-text">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((row, i) => (
                    <tr key={row.id ?? i} className="qp-table__row">
                      <td
                        className="qp-table__td upsell-indicator-history__client-name-cell"
                      >
                        {row.name}
                      </td>
                      <td className="qp-table__td upsell-indicator-history__nowrap-cell">
                        {formatDate(row.created)}
                      </td>
                      <td className="qp-table__td">{row.lob}</td>
                      <td className="qp-table__td upsell-indicator-history__center-text">
                        <div className="qp-table__actions">
                          <button
                            className="qp-table__action-btn"
                            title="View Upsell Summary"
                            onClick={() => openSummary(row)}
                          >
                            <img src={UpsellIcon} alt="View" width={30} height={30} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {filtered.length > PAGE_SIZE && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, idx) =>
                  p === '…'
                    ? <span key={`ell-${idx}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          summary={summary}
          loading={previewLoading}
          onClose={closePreview}
        />
      )}

      <style>{`@keyframes upsell-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default UpsellIndicatorHistory;

